import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  WorkspaceDocument,
  WorkspaceFolder,
} from "@/models/workspace/Workspace";
import Workspace from "@/models/workspace/Workspace";
import isEqual from "lodash/isEqual";
import { showWarningToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import {
  countDedupedDocumentsInExplorerSelection,
  MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS,
  type ExplorerSelectionEntry,
} from "@/lib/buildConversationWorkspaceDocuments";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import {
  collectMatchingItems as sharedCollectMatchingItems,
  isDocumentSelectableForChat as sharedIsDocumentSelectableForChat,
  nextSelectionAfterToggle as sharedNextSelectionAfterToggle,
  withoutWorkspace as sharedWithoutWorkspace,
} from "@capacit-com/lara-law-shared-ui";
import type {
  WorkspaceDocument as SharedWorkspaceDocument,
  WorkspaceFolder as SharedWorkspaceFolder,
  WorkspaceSelectionEntry as SharedSelectionEntry,
  WorkspaceSelectionToggle as SharedSelectionToggle,
} from "@capacit-com/lara-law-shared-ui";

/** Documents must be indexed AND not historic (Gældende = Nej) to be used as chat context. */
const isDocumentSelectableForChat = (doc: WorkspaceDocument): boolean =>
  sharedIsDocumentSelectableForChat(doc as unknown as SharedWorkspaceDocument);

const collectMatchingItems = (
  folders: WorkspaceFolder[],
  ids: string[]
): (WorkspaceDocument | WorkspaceFolder)[] =>
  sharedCollectMatchingItems(
    folders as unknown as SharedWorkspaceFolder[],
    ids,
    (d) => isDocumentSelectableForChat(d as unknown as WorkspaceDocument)
  ) as unknown as (WorkspaceDocument | WorkspaceFolder)[];

/** The shared selection rule, under web-app's Workspace types. */
const nextSelection = (
  prev: ExplorerSelectionEntry[],
  item: WorkspaceDocument | WorkspaceFolder,
  workspaceId: string
): ExplorerSelectionEntry[] =>
  sharedNextSelectionAfterToggle(
    prev as unknown as SharedSelectionEntry[],
    { item, workspaceId } as unknown as SharedSelectionToggle,
    (d) => isDocumentSelectableForChat(d as unknown as WorkspaceDocument)
  ) as unknown as ExplorerSelectionEntry[];

/**
 * The shared toggle plus chat's own gate: a selection that would put more than
 * {@link MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS} documents in the
 * conversation context is refused whole.
 */
function computeNextSelectionAfterToggle(
  prev: ExplorerSelectionEntry[],
  item: WorkspaceDocument | WorkspaceFolder,
  workspaceId: string
): { next: ExplorerSelectionEntry[]; capRejected: boolean } {
  const next = nextSelection(prev, item, workspaceId);
  if (
    countDedupedDocumentsInExplorerSelection(next) >
    MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS
  ) {
    // New array reference so React commits (returning `prev` can bail out and skip layout effects).
    return { next: [...prev], capRejected: true };
  }
  return { next, capRejected: false };
}

interface UseWorkspaceDialogSyncProps {
  workspaceDialogOpen: boolean;
  workspaces: Workspace[];
}

export const useWorkspaceDialogSync = ({
  workspaceDialogOpen,
  workspaces,
}: UseWorkspaceDialogSyncProps) => {
  const { workspaceContext, setWorkspaceContext } =
    useNewConversationFiltersStore();
  const [selectedItems, setSelectedItems] = useState<ExplorerSelectionEntry[]>(
    []
  );
  const pendingCapToastRef = useRef(false);
  const prevDialogOpenRef = useRef<boolean>(false);
  const initialContextRef = useRef<typeof workspaceContext>({});

  // Load initial context when dialog opens
  useEffect(() => {
    const dialogJustOpened = workspaceDialogOpen && !prevDialogOpenRef.current;
    prevDialogOpenRef.current = workspaceDialogOpen;

    if (!dialogJustOpened || !workspaces.length) {
      return;
    }

    // Store the initial context for reset
    initialContextRef.current = workspaceContext;

    const selected: (WorkspaceDocument | WorkspaceFolder)[] = [];

    // Start with current context; we will replace per-workspace with filtered lists where we have workspace data
    const filteredContext: typeof workspaceContext = { ...workspaceContext };

    workspaces.forEach((workspace) => {
      const ids = workspaceContext[workspace.id];
      if (!ids) return;

      const filteredIds: Array<{
        documentId: string;
        activeRevisionNumber?: number;
      }> = [];

      if (workspace.documents) {
        workspace.documents.forEach((doc) => {
          if (
            ids.map((d) => d.documentId).includes(doc.id) &&
            isDocumentSelectableForChat(doc)
          ) {
            selected.push(doc);
            filteredIds.push({
              documentId: doc.id,
              activeRevisionNumber: doc.activeRevision,
            });
          }
        });
      }

      const fromFolders = collectMatchingItems(
        workspace.folders,
        ids.map((d) => d.documentId)
      );
      fromFolders.forEach((item) => {
        selected.push(item);
        filteredIds.push({
          documentId: item.id,
          activeRevisionNumber:
            "activeRevision" in item ? item.activeRevision : undefined,
        });
      });

      filteredContext[workspace.id] = filteredIds;
    });

    // Remove historic documents from stored context when dialog opens
    if (!isEqual(workspaceContext, filteredContext)) {
      setWorkspaceContext(filteredContext);
    }

    const newSelectedItems = selected.map((item) => ({
      item,
      workspaceId:
        workspaces.find((ws) =>
          [
            ...ws.documents,
            ...collectMatchingItems(
              ws.folders,
              workspaceContext[ws.id]?.map((doc) => doc.documentId) || []
            ),
          ].some((i) => i.id === item.id)
        )?.id ?? "",
    }));

    setSelectedItems(newSelectedItems);
  }, [workspaceDialogOpen, workspaces, workspaceContext, setWorkspaceContext]);

  useLayoutEffect(() => {
    if (!pendingCapToastRef.current) return;
    pendingCapToastRef.current = false;
    logger.warn("Workspace context selection rejected at document cap", {
      operation: "useWorkspaceDialogSync.selectItem",
      maxAllowed: MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS,
    });
    showWarningToast(
      `Du kan højst vælge ${MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS} dokumenter i konteksten (unikke pr. workspace). Fjern nogle filer før du tilføjer flere.`,
      "Maks. dokumenter i kontekst"
    );
  });

  const handleSelectItem = (
    item: WorkspaceDocument | WorkspaceFolder,
    workspaceId: string
  ) => {
    setSelectedItems((prev) => {
      const { next, capRejected } = computeNextSelectionAfterToggle(
        prev,
        item,
        workspaceId
      );
      if (capRejected) {
        pendingCapToastRef.current = true;
      }
      return next;
    });
  };

  const handleDeselectWorkspace = (workspaceId: string) => {
    setSelectedItems(
      (prev) =>
        sharedWithoutWorkspace(
          prev as unknown as SharedSelectionEntry[],
          workspaceId
        ) as unknown as ExplorerSelectionEntry[]
    );
  };

  /**
   * Explorer's "select all workspaces" row hands over every selectable entry it
   * would tick (or an empty list when unticked). Replacing the selection rather
   * than toggling item by item means a partly selected tenant fills up instead
   * of inverting.
   *
   * The cap is judged on the whole result: past it the selection is left alone,
   * because a partial "select all" would leave folders ticked whose documents
   * were dropped.
   */
  const handleSelectAllWorkspaces = (entries: ExplorerSelectionEntry[]) => {
    const documentCount = countDedupedDocumentsInExplorerSelection(entries);
    if (documentCount > MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS) {
      logger.warn("Select-all-workspaces rejected at document cap", {
        operation: "useWorkspaceDialogSync.selectAllWorkspaces",
        documentCount,
        workspaceCount: workspaces.length,
        maxAllowed: MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS,
      });
      showWarningToast(
        `Alle dine workspaces indeholder tilsammen ${documentCount} dokumenter, og der kan højst være ${MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS} i konteksten. Vælg de workspaces eller mapper du har brug for.`,
        "Maks. dokumenter i kontekst"
      );
      return;
    }

    setSelectedItems(entries);
  };

  const commitSelection = () => {
    const newContext: Record<
      string,
      Array<{ documentId: string; activeRevisionNumber?: number }>
    > = {};

    for (const { item, workspaceId } of selectedItems) {
      if ("activeRevision" in item && !isDocumentSelectableForChat(item)) {
        continue;
      }
      if (!newContext[workspaceId]) {
        newContext[workspaceId] = [];
      }
      if ("activeRevision" in item) {
        newContext[workspaceId].push({
          documentId: item.id,
          activeRevisionNumber: item.activeRevision,
        });
      } else {
        newContext[workspaceId].push({
          documentId: item.id,
          activeRevisionNumber: undefined,
        });
      }
    }

    if (!isEqual(workspaceContext, newContext)) {
      setWorkspaceContext(newContext);
    }
  };

  const resetSelection = () => {
    // Reset to the initial context when dialog was opened
    setWorkspaceContext(initialContextRef.current);
    setSelectedItems([]);
  };

  return {
    selectedItems,
    handleSelectItem,
    handleDeselectWorkspace,
    handleSelectAllWorkspaces,
    commitSelection,
    resetSelection,
  };
};
